// @ts-nocheck
import PropTypes from "prop-types";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {BaseButton, BaseInput, BaseModal, toast} from "@/components";
import {useBinanceTransactionStatus, usePostBinanceDeposit} from "../../../hooks/wallet/usePostBinanceDeposit";

const BinanceQRModal = ({visible, onClose, qrUrl, paymentUrl, orderId, amount}) => {
  const {data: transactionStatusResponse, error, isLoading} = useBinanceTransactionStatus(orderId, visible);
  const {t} = useTranslation();

  const transactionData = transactionStatusResponse?.data?.data;
  const actualStatus = transactionData?.status;

  useEffect(() => {
    if (actualStatus === "completed" || actualStatus === "success") {
      toast.success(t("depositInstructionsBinancePaymentCompleted"));
      const id = setTimeout(() => onClose(), 5000);
      return () => clearTimeout(id);
    }
    if (error) {
      // silent
    }
  }, [actualStatus, onClose, error, t]);

  return (
    <BaseModal
      open={visible}
      onClose={onClose}
      title={t("depositInstructionsBinanceCompletePayment")}
      size="md"
      className="coronando-sidebar-scrollbar"
    >
      <div className="mx-auto w-full max-w-[440px] text-center py-2">
        <div className="mb-5 rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-4 py-3">
          <h3 className="m-0 text-[var(--font-h3)] font-bold text-[var(--color-text)]">
            {t("depositInstructionsBinanceAmount")}: {amount} USDT
          </h3>
          <p className="mt-2 mb-0 text-[var(--font-body)] text-[var(--color-text-secondary)]">
            {t("depositInstructionsBinanceOptions")}
          </p>
        </div>

        <h4 className="mb-3 text-[var(--font-h4)] font-semibold text-[var(--color-text)]">
          {t("depositInstructionsBinanceOptionsTitle")}
        </h4>

        {qrUrl && (
          <div className="mb-5">
            <p className="mb-3 text-[var(--font-body)] text-[var(--color-text-secondary)]">
              {t("depositInstructionsBinanceUseQR")}
            </p>
            <div className="mx-auto w-full max-w-[320px] rounded-2xl border border-[var(--color-border)] bg-white p-3 shadow-[var(--shadow-elev-1)]">
              <img
                src={qrUrl}
                alt="Payment QR Code"
                className="w-full rounded-lg"
              />
            </div>
          </div>
        )}

        {paymentUrl && (
          <div className="mb-4">
            <p className="mb-3 text-[var(--font-body)] text-[var(--color-text-secondary)]">
              {t("depositInstructionsBinanceLink")}
            </p>
            <a
              href={paymentUrl}
              target="_blank"
              rel="noreferrer"
              className="block w-full"
            >
              <BaseButton
                variant="primary"
                fullWidth
                className="!rounded-full"
              >
                {t("depositInstructionsBinanceOpenLink")}
              </BaseButton>
            </a>
          </div>
        )}

        <div className="mt-5 rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-4 py-3">
          <p className="m-0 text-[var(--font-body)] text-[var(--color-text-secondary)]">
            {t("depositInstructionsBinanceStatus")}:{" "}
            <span className="font-semibold text-[var(--color-primary)]">
              {isLoading
                ? t("depositInstructionsBinanceCheckingStatus")
                : actualStatus || t("depositInstructionsBinancePending")}
            </span>
          </p>
        </div>
      </div>
    </BaseModal>
  );
};

const BinanceOptions = ({prev}) => {
  const [amount, setAmount] = useState("");
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [depositData, setDepositData] = useState(null);
  const binanceDepositMutation = usePostBinanceDeposit();
  const {t} = useTranslation();

  const handleAmountChange = e => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) setAmount(value);
  };

  const handleBack = () => {
    setAmount("");
    if (prev) prev();
  };

  const handleContinue = async () => {
    if (!amount || parseFloat(amount) < 10) {
      toast.error(t("depositInstructionsBinanceValidAmount"));
      return;
    }
    try {
      const response = await binanceDepositMutation.mutateAsync(amount);
      if (response?.data && response?.data?.status === 1) {
        setDepositData(response.data.data);
        setQrModalVisible(true);
        toast.success(t("depositInstructionsBinanceGeneratedLink"));
      } else {
        toast.error(response?.data?.message || t("depositInstructionsBinanceError"));
      }
    } catch {
      toast.error(t("depositInstructionsBinanceError"));
    }
  };

  const handleCloseQRModal = () => {
    setQrModalVisible(false);
    setDepositData(null);
    setAmount("");
  };

  return (
    <div>
      <h4 style={{marginBottom: "var(--space-5)"}}>{t("depositInstructionsBinanceTitle")}</h4>
      <div style={{marginBottom: "var(--space-5)"}}>
        <label
          style={{
            display: "block",
            marginBottom: "var(--space-2-5)",
            color: "var(--color-text-secondary)",
            fontWeight: 500,
          }}
        >
          {t("depositInstructionsBinanceAmount")}
        </label>
        <BaseInput
          type="text"
          value={amount}
          onChange={handleAmountChange}
          placeholder={t("depositInstructionsBinanceEnterAmount")}
          containerClassName="mb-[var(--space-3)]"
          className="!border-2 !border-[var(--color-lottery)] focus-visible:!outline-[var(--color-lottery)] hover:!border-[var(--color-lottery-soft)]"
        />
        <p style={{fontSize: "var(--font-body)", color: "var(--color-text-secondary)", margin: 0}}>
          {t("depositInstructionsBinanceMinAmount")}: $10.00 USDT
        </p>
      </div>
      <div style={{display: "flex", gap: "var(--space-3-5)"}}>
        <BaseButton
          variant="primary"
          className="!rounded-full !bg-[var(--color-lottery)] !text-white hover:!bg-[var(--color-lottery-soft)]"
          onClick={handleContinue}
          loading={binanceDepositMutation.isPending}
          disabled={!amount || parseFloat(amount) < 10}
          style={{flex: 1}}
        >
          {t("depositInstructionsBinanceContinue")}
        </BaseButton>
        <BaseButton
          variant="outline"
          onClick={handleBack}
          className="!rounded-full [--btn-outline-color:var(--color-lottery)] [--btn-outline-fill:var(--color-lottery)] [--btn-outline-text-hover:#ffffff] [--btn-outline-glow:rgba(255,3,96,0.45)]"
          style={{flex: 1}}
        >
          {t("depositInstructionsBinanceBack")}
        </BaseButton>
      </div>
      <BinanceQRModal
        visible={qrModalVisible}
        onClose={handleCloseQRModal}
        qrUrl={depositData?.payment_qr}
        paymentUrl={depositData?.payment_url}
        orderId={depositData?.order_id}
        amount={amount}
      />
    </div>
  );
};

BinanceOptions.propTypes = {
  prev: PropTypes.func,
};

BinanceQRModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  qrUrl: PropTypes.string,
  paymentUrl: PropTypes.string,
  orderId: PropTypes.string,
  amount: PropTypes.string,
};

export default BinanceOptions;
